import { fetchPatientQueryOptions } from '@avanta/shared/hooks/useFetchPatient';
import { useServerPaginatedTable } from '@avanta/shared/hooks/useServerPaginatedTable';
import { getPatientName } from '@avanta/shared/utils/appointments';
import { isLoadingNewData } from '@avanta/shared/utils/table';
import { useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import { type ColumnDef, type Table } from '@tanstack/react-table';
import { useState } from 'react';
import { EMPTY_VALUE, QUERY_KEYS } from 'constants/common';
import { useFetchReportNotificationsCount } from 'hooks/useFetchReportNotificationsCount';
import { AppointmentService } from 'services/appointments';
import { useEnterpriseId } from 'stores/user';
import { type ReportNotificationAppointment } from 'types/appointments';

const DEFAULT_PAGE_SIZE = 20;

export interface ReportNotificationRow extends ReportNotificationAppointment {
  patientName: string;
  patientNif: string;
}

export const useFetchReportNotifications = (
  columns: ColumnDef<ReportNotificationRow>[]
): {
  table: Table<ReportNotificationRow>;
  query: UseQueryResult<ReportNotificationRow[], Error>;
  pendingCount: number;
  isLoading: boolean;
} => {
  const enterpriseId = useEnterpriseId();
  const queryClient = useQueryClient();
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: DEFAULT_PAGE_SIZE,
  });
  const countQuery = useFetchReportNotificationsCount();

  const query = useQuery({
    queryKey: [
      QUERY_KEYS.FETCH_REPORT_NOTIFICATIONS,
      enterpriseId,
      false,
      pagination.pageIndex,
      pagination.pageSize,
    ],
    queryFn: async () => {
      const { data: notifications } = await AppointmentService.getReportNotifications(
        enterpriseId,
        {
          verified: false,
          offset: pagination.pageIndex,
          limit: pagination.pageSize,
        }
      );

      const notificationResults = await Promise.allSettled(
        notifications.map(async notification => {
          const patient = await queryClient.fetchQuery(
            fetchPatientQueryOptions(notification.patientId)
          );

          return {
            ...notification,
            patientName: getPatientName(patient),
            patientNif: patient.nif || EMPTY_VALUE,
          };
        })
      );

      return notificationResults.flatMap(result =>
        result.status === 'fulfilled' ? [result.value] : []
      );
    },
    placeholderData: previousData => previousData,
    enabled: !!enterpriseId,
  });

  const table = useServerPaginatedTable<ReportNotificationRow>({
    columns,
    data: query.data ?? [],
    totalCount: countQuery.data ?? 0,
    pagination,
    onPaginationChange: setPagination,
    globalFilter: '',
    onGlobalFilterChange: () => undefined,
    isLoadingData: isLoadingNewData(query) || isLoadingNewData(countQuery),
  });

  return {
    table,
    query,
    pendingCount: countQuery.data ?? 0,
    isLoading: countQuery.isLoading || query.isLoading,
  };
};
